import siteOperationTypesStyles from './site-operation-types-section.styles.css?inline';
import '../../../components/state-badge/agwa-state-badge.js';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { live } from 'lit/directives/live.js';
import { customElement, property, state } from 'lit/decorators.js';
import { ApiFactory } from '../../../api/agwasuite-api-factory';
import type { ApiResponse } from '../../../api/agwasuite-api';
import { ApiSiteMaintenance } from '../../../api/core/api-site-maintenance';
import { SecurityApi } from '../../../api/security/security-api';
import { toErrorMessage } from '../../../api/error-utils';
import type {
  SecurityDomainDto,
  SiteDomainScopeDto,
} from '../../../api/agwa-api-model';
import { confirmAction } from '../../../components/dialogs/agwa-confirm-dialog';
import { styles as sharedStyles } from '../../../styles/shared-styles';
import {
  getOrganizationTypeDescription,
  getOrganizationTypeLabel,
  grantsCompanyRegister,
  grantsWorkArea,
} from '../shared/organization-type-labels';
import { DomainId } from '../../../utility/claim-id';
import { isSame } from '../../../utility/strings/strings-utils';
import { terms } from '../../../language/terms';
import { SHOW_UNASSIGNABLE_OPERATION_TYPES } from '../shared/operation-type-visibility';

import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/checkbox/checkbox.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';
import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';

/**
 * The site's operation types as one lean list: every type AGWA defines, with weight on the ones
 * this site holds.
 *
 * WHY IT REPLACES A PAGE OF CARDS. The previous panel gave each type a card, a paragraph and a
 * "Register as X" button, so the nine registrable types filled a screen and the two or three a site
 * actually IS were no more findable than the six it is not. The people who use this software learn
 * what the types mean; what they need at a glance is which ones apply here. So the description
 * moves behind an expander, and the answer to "what is this site" is the emphasis in the list
 * itself.
 *
 * REGISTERING AND DEACTIVATING ARE ONE CONTROL. A checkbox states the fact - this site is a
 * fabricator, or it is not - and ticking or unticking is what changes it. That is only honest
 * because the destructive direction is guarded: unticking hard-deletes ONLY where the server says
 * nothing depends on the registration, and otherwise deactivates, which retains every row. For the
 * two types that give the {@link terms.loginGroup.lower} its register, "nothing depends on it"
 * reaches past this {@link terms.loginSite.lower}: the server answers on what the register holds,
 * so a register with anything on it puts every one of the company's registrations on the
 * deactivate path.
 *
 * BOTH DIRECTIONS CONFIRM, AND THE ARCHITECT'S REASON IS THE ONE THAT MATTERS (2026-08-30):
 * deactivation is not destructive to data, but it IS destructive to the people using the software,
 * who lose a work area and the content inside it. A checkbox that quietly took a work area away
 * would be the worst kind of small control. Each dialog therefore states which of the four things
 * is about to happen rather than warning in the abstract.
 */
/**
 * The two operation types that describe the same business from two sides, each mapped to the other.
 *
 * A site may hold both and AGWA leaves the combination alone ([D-127]: "leave as is"), but it is
 * worth a word when someone adds the second, so `pairCaution` looks the partner up here. A map
 * rather than a pair of ifs, for the reason `collapseOperationTypeCodes` gives for the same pair:
 * the next combination AGWA names is then a line of data rather than another branch. Keys are
 * lower-cased because the code being looked up arrives from an API response.
 */
const OPERATION_TYPE_PAIR: ReadonlyMap<string, string> = new Map([
  [
    DomainId.primeSystemSupplier.toLowerCase(),
    DomainId.primeSystemManufacturer,
  ],
  [
    DomainId.primeSystemManufacturer.toLowerCase(),
    DomainId.primeSystemSupplier,
  ],
]);

@customElement('site-operation-types-section')
export class SiteOperationTypesSection extends LitElement {
  static styles = [sharedStyles, unsafeCSS(siteOperationTypesStyles)];

  @property({ type: String }) siteId = '';
  @property({ type: String }) siteName = '';

  @state() private loading = false;
  @state() private error: string | null = null;
  @state() private domains: SecurityDomainDto[] = [];
  @state() private scopes: SiteDomainScopeDto[] = [];
  @state() private busyCode: string | null = null;
  @state() private statusMessage: string | null = null;
  @state() private statusVariant: 'success' | 'danger' = 'success';

  protected updated(changed: Map<string, unknown>) {
    if (changed.has('siteId')) {
      if (this.siteId) {
        void this.loadData();
      } else {
        this.domains = [];
        this.scopes = [];
      }
    }
  }

  private async loadData() {
    if (!this.siteId) return;
    this.loading = true;
    this.error = null;
    try {
      const securityApi = ApiFactory.authenticated(SecurityApi);
      const siteApi = ApiFactory.authenticated(ApiSiteMaintenance);

      const [domainsResp, scopesResp] = await Promise.all([
        securityApi.getSecurityDomains(),
        siteApi.getSiteDomains(this.siteId),
      ]);

      if (!domainsResp.success) throw new Error(toErrorMessage(domainsResp));
      if (!scopesResp.success) throw new Error(toErrorMessage(scopesResp));

      this.domains = domainsResp.data?.domains ?? [];
      this.scopes = scopesResp.data?.scopes ?? [];
    } catch (error) {
      this.error =
        (error as Error)?.message ??
        `Unable to load ${terms.userGroupDomain.lowerPlural}.`;
    } finally {
      this.loading = false;
    }
  }

  /**
   * Every site-level type, in the order the server sent them.
   *
   * The order is `UserGroupDomain.DisplayOrder`, which the listing job already sorts by, so this
   * deliberately does not re-sort: a second ordering here would be a second opinion about the same
   * question, and the two would drift.
   */
  private get listedDomains(): SecurityDomainDto[] {
    return this.domains.filter((domain) => {
      if (!domain.code) return false;
      if (domain.scopeLevel !== 'Site') return false;
      // The delegation / internal domains stay hidden unless the switch is on, exactly as the
      // panel this replaces had it. See operation-type-visibility.ts for why.
      if (domain.uiAssignable === false) {
        return SHOW_UNASSIGNABLE_OPERATION_TYPES;
      }
      return true;
    });
  }

  private scopeFor(domainCode: string): SiteDomainScopeDto | undefined {
    return this.scopes.find((scope) => scope.domainCode === domainCode);
  }

  /**
   * The checkbox. Four outcomes, and each one asks a question that names its own consequence.
   */
  private async handleToggle(domain: SecurityDomainDto, checked: boolean) {
    const code = domain.code;
    const scope = this.scopeFor(code);
    const label = getOrganizationTypeLabel(code);
    const site = this.siteName || `this ${terms.loginSite.lower}`;

    if (checked) {
      // Registering, or bringing a deactivated registration back.
      if (scope && !scope.active) {
        // The pair's reactivation says what comes back and, as pointedly, what does not: nobody's
        // access to the register changes, because being at this site was never what granted it.
        const reactivateMessage = grantsCompanyRegister(code)
          ? `Make ${site} a ${label} again? The ${terms.loginGroup.lower} holds its ${terms.primeSystemIntellectualProperty.value} ${terms.productRegister.lower} again, with everything recorded on it before. Who may edit it does not change: that is set ${terms.loginUser.lower} by ${terms.loginUser.lower} on the ${terms.loginGroup.lowerPossessive} ${terms.staffUser.lower} page.${this.pairCaution(code)}`
          : `Make ${site} a ${label} again? Everything recorded against it before becomes visible once more, and anyone assigned to this ${terms.loginSite.lower} regains access to it.`;

        const confirmed = await confirmAction({
          title: `Reactivate ${label}`,
          message: reactivateMessage,
          confirmText: 'Reactivate',
          cancelText: 'Cancel',
          variant: 'primary',
        });
        if (!confirmed) return this.requestUpdate();
        await this.run(code, (api) =>
          api.reactivateSiteDomain(this.siteId, scope.scopeId)
        );
        return;
      }

      // A new registration.
      const confirmed = await confirmAction({
        title: `Register as ${label}`,
        message: this.registrationMessage(code, label, site),
        confirmText: `Yes, register as ${label}`,
        cancelText: 'Cancel',
        variant: 'warning',
      });
      if (!confirmed) return this.requestUpdate();

      await this.run(code, (api) =>
        api.attachSiteDomain(this.siteId, {
          loginSiteId: this.siteId,
          domainCode: code,
          autoGrantDefaults: true,
          active: true,
        })
      );
      return;
    }

    // Unticking. Nothing to do if it was never registered.
    if (!scope) return this.requestUpdate();

    if (scope.canHardDelete) {
      const confirmed = await confirmAction({
        title: `Remove ${label}`,
        message: this.removalMessage(code, label, site),
        confirmText: 'Remove',
        cancelText: 'Cancel',
        variant: 'danger',
      });
      if (!confirmed) return this.requestUpdate();
      await this.run(code, (api) =>
        api.hardDeleteSiteDomain(this.siteId, scope.scopeId)
      );
      return;
    }

    const confirmed = await confirmAction({
      title: `Deactivate ${label}`,
      message: this.deactivationMessage(code, label, site),
      confirmText: 'Deactivate',
      cancelText: 'Cancel',
      variant: 'warning',
    });
    if (!confirmed) return this.requestUpdate();
    await this.run(code, (api) =>
      api.deactivateSiteDomain(this.siteId, scope.scopeId)
    );
  }

  /**
   * WHAT REGISTERING THIS TYPE ACTUALLY DOES, in the words the person about to do it needs.
   *
   * THREE PROMISES, because since [D-127] there are three kinds of type and one sentence for all
   * of them would be false about at least one:
   *
   * - A {@link grantsWorkArea} type gives THIS SITE a work area, and everyone assigned to the
   *   site reaches it. Prime Glass Manufacturer is the only one.
   * - A {@link grantsCompanyRegister} type - the supplier and the manufacturer - gives the
   *   COMPANY its Prime System IP register, held on a site nobody sees, and gives NOBODY access
   *   to it. That second half is the load-bearing one: the right to edit is granted person by
   *   person on the staff page (D-127 rule 6), so the sentence this replaced - "everyone assigned
   *   here gains access to it" - told AGWA staff they had granted something they had not. Someone
   *   acting on it would leave a person without the access they meant to give, or believe they
   *   had withheld access that was never theirs to withhold here (`@PIP-EDIT-003`).
   * - Everything else records a classification and grants nothing at all.
   *
   * THE PERMANENCE CLAUSE IS ABOUT THE COMPANY'S REGISTER, not about this registration, and that
   * distinction is the whole of what changed on 2026-09-18. The sentence read "Once
   * {@link terms.product.lowerPlural} are recorded on it the registration can be deactivated but
   * never removed", which the server did not enforce: its gate answered "no data" for these two
   * types unconditionally, so Remove was offered whatever the register held. The gate now answers on
   * the register, so the sentence says both halves of what is true - removable while the register is
   * empty, deactivate-only once it is not - and the Deactivate path it points at is reachable.
   */
  private registrationMessage(
    code: string,
    label: string,
    site: string
  ): string {
    if (grantsWorkArea(code)) {
      return `Register ${site} as a ${label}? This creates its own work area on this ${terms.loginSite.lower}, and everyone assigned here gains access to it. Once records exist in that work area the registration can be deactivated but never removed.`;
    }

    if (grantsCompanyRegister(code)) {
      return `Register ${site} as a ${label}? This gives the ${terms.loginGroup.lower} its ${terms.primeSystemIntellectualProperty.value} ${terms.productRegister.lower}, which belongs to the ${terms.loginGroup.lower} rather than to this ${terms.loginSite.lower}. Nobody gains access to it today: the right to edit it is given to each ${terms.loginUser.lower} on the ${terms.loginGroup.lowerPossessive} ${terms.staffUser.lower} page. While the ${terms.loginGroup.lowerPossessive} ${terms.productRegister.lower} holds nothing the registration can be removed cleanly; once ${terms.product.lowerPlural} are recorded on it the registration can be deactivated but never removed.${this.pairCaution(code)}`;
    }

    return `Register ${site} as a ${label}? This records a classification and grants no work area yet, so nobody's access changes today. It can be removed cleanly while nothing depends on it.`;
  }

  /**
   * What removing it outright actually does, which for the pair is not what it does for anything
   * else.
   *
   * WHY THE PAIR NEEDS ITS OWN SENTENCE. The shared message says "Nothing has been recorded against
   * it", and for a Prime System Supplier or Prime System Manufacturer that is a claim about the
   * wrong thing: the registration itself never holds records, so it would be true of one holding a
   * register with a thousand {@link terms.product.lowerPlural} on it. What actually gates the
   * removal is the COMPANY's register being empty (`PrimeSystemRegistrationScopeHandler`), so the
   * pair's sentence names the register rather than the registration, and says plainly that this is
   * the only moment a removal is offered at all.
   *
   * Every other type keeps the original message verbatim: for them the claim is about the thing
   * being removed and it is true.
   */
  private removalMessage(code: string, label: string, site: string): string {
    if (grantsCompanyRegister(code)) {
      return `Remove ${label} from ${site}? The ${terms.loginGroup.lowerPossessive} ${terms.primeSystemIntellectualProperty.value} ${terms.productRegister.lower} holds nothing, which is the only time this registration can be removed at all: once ${terms.product.lowerPlural} are recorded on it, it can be deactivated but never removed. The ${terms.loginSite.lower} reads as though it was never registered, and it can be registered again cleanly. This cannot be undone.`;
    }

    return `Remove ${label} from ${site}? Nothing has been recorded against it, so this removes the registration outright and the ${terms.loginSite.lower} reads as though it was never registered. It can be registered again cleanly. This cannot be undone.`;
  }

  /**
   * What deactivating it takes away, which is never the same thing as what registering it gave.
   *
   * The pair's sentence is CONDITIONAL and says so, because one site's registration is not on its
   * own what keeps the register alive: D-127 rule 3 deactivates the Prime System IP site when the
   * last active supplier or manufacturer registration in the company goes, so unticking here
   * closes the register only when nothing else holds it open. Stating it flatly would frighten
   * someone off a change that costs their company nothing, and omitting it would hide the one
   * consequence that reaches beyond this site.
   */
  private deactivationMessage(
    code: string,
    label: string,
    site: string
  ): string {
    if (grantsWorkArea(code)) {
      return `Deactivate ${label} for ${site}? Its work area disappears from this ${terms.loginSite.lower} for everyone assigned here, and its records drop out of searches, listings and the public API. Nothing is deleted: every row and its history are kept, and reactivating restores access.`;
    }

    if (grantsCompanyRegister(code)) {
      return `Deactivate ${label} for ${site}? The ${terms.loginSite.lower} stops being listed as a ${label}. If this was the ${terms.loginGroup.lowerPossessive} last active ${terms.primeSystemSupplier.value} or ${terms.primeSystemManufacturer.value} registration, its ${terms.primeSystemIntellectualProperty.value} ${terms.productRegister.lower} closes with it and drops out of searches, listings and the public API. Nothing is deleted: every row and its history are kept, and registering again brings the same ${terms.productRegister.lower} back.`;
    }

    return `Deactivate ${label} for ${site}? The ${terms.loginSite.lower} stops being listed as a ${label} and drops out of searches and the public API. Nothing is deleted, the history is kept, and it can be reactivated later.`;
  }

  /**
   * The extra sentence a site that already holds the other half of the pair is given
   * (`@MEM-TYPES-012`).
   *
   * ADDED TO the registration sentence, never substituted for it. The caution is about an unusual
   * COMBINATION, not about a different act, so the act's own consequences still have to be stated;
   * and two journeys read the sentence a replacement would displace, which would turn them red for
   * a reason that has nothing to do with what changed. The scenario's own intent note says both.
   *
   * Empty when there is nothing to caution, so the caller concatenates unconditionally. A
   * DEACTIVATED registration of the partner is not one the site holds - rule 3's whole point is
   * that an inactive scope counts for nothing while it sleeps - so only an active one cautions.
   */
  private pairCaution(code: string): string {
    const partner = OPERATION_TYPE_PAIR.get(code.trim().toLowerCase());
    if (!partner) return '';

    const holdsPartner = this.scopes.some(
      (scope) => isSame(scope.domainCode ?? '', partner) && scope.active
    );
    if (!holdsPartner) return '';

    return ` Holding both ${terms.primeSystemSupplier.value} and ${terms.primeSystemManufacturer.value} on one ${terms.loginSite.lower} is unusual and unnecessary, because a ${terms.primeSystemManufacturer.value} already supplies its own system. Registering it anyway is allowed and gives the ${terms.loginGroup.lower} nothing it does not already have.`;
  }

  private async run(
    domainCode: string,
    action: (api: ApiSiteMaintenance) => Promise<ApiResponse<unknown>>
  ) {
    this.busyCode = domainCode;
    this.statusMessage = null;
    try {
      const api = ApiFactory.authenticated(ApiSiteMaintenance);
      const response = await action(api);
      if (!response.success) throw new Error(toErrorMessage(response));
      await this.loadData();
      this.dispatchEvent(
        new CustomEvent('domains-updated', { bubbles: true, composed: true })
      );
    } catch (error) {
      this.statusMessage =
        (error as Error)?.message ?? 'The change could not be completed.';
      this.statusVariant = 'danger';
      // The checkbox showed the requested state optimistically; reloading puts it back to what the
      // server actually holds, so a failed change never leaves a tick claiming something untrue.
      await this.loadData();
    } finally {
      this.busyCode = null;
    }
  }

  private renderRow(domain: SecurityDomainDto) {
    const code = domain.code;
    const scope = this.scopeFor(code);
    const registered = !!scope;
    const active = !!scope?.active;
    const label = getOrganizationTypeLabel(code);
    const description = getOrganizationTypeDescription(code);

    return html`
      <div
        class="operation-type-row ${active ? 'is-registered' : ''} ${
          registered && !active ? 'is-deactivated' : ''
        }"
      >
        <div class="operation-type-head">
          <!--
            THE NAME IS THE CHECKBOX'S OWN LABEL, not a span beside it. A checkbox whose accessible
            name is empty is unreachable by a screen reader and unaddressable by any test that asks
            for a control by what it says - and "tick the Fabricator box" is precisely how both a
            person and a journey think about this row.
          -->
          <!--
            live() rather than a plain ?checked binding: ticking the box changes the DOM's own
            checked state, but the source of truth (active) has not changed, so a plain binding is
            dirty-checked as unchanged and Lit will not undo the tick when a confirm is cancelled.
            live() compares against the live DOM value, so the requestUpdate() on every cancel path
            resets the box to what the server actually holds instead of leaving a false tick.
          -->
          <sl-checkbox
            class="operation-type-name"
            .checked=${live(active)}
            ?disabled=${this.busyCode !== null}
            @sl-change=${(event: Event) =>
              void this.handleToggle(
                domain,
                (event.target as HTMLInputElement).checked
              )}
          >
            <!--
              THE CODE THEN THE NAME - "PSS Prime System Supplier". The abbreviation is what AGWA
              staff say and what the site header shows, so leading with it makes the two surfaces
              one vocabulary; the name follows for everyone who has not learned them yet. Both sit
              inside the checkbox's label, so the accessible name is the whole phrase and clicking
              either part toggles the row.
            -->
            ${
              domain.shortCode
                ? html`<span class="operation-type-code"
                    >${domain.shortCode}</span
                  >`
                : nothing
            }${label}</sl-checkbox
          >

          ${
            registered && !active
              ? html`<agwa-state-badge
                  domain="siteOperationType"
                  state="Deactivated"
                ></agwa-state-badge>`
              : nothing
          }
          ${this.busyCode === code ? html`<sl-spinner></sl-spinner>` : nothing}

          <!--
            WHAT IT MEANS IS ONE HOVER AWAY, not one click and a reflow. An expander moved every row
            below it down the page to show two lines of explanatory copy, which is a large motion for
            a small answer - and in two columns it would shunt its neighbour as well. The icon marks
            which rows have something to say and the tooltip says it in place.
          -->
          ${
            description
              ? html`
                  <!--
                    NOT hoisted. Hoisting renders the popup outside this row, which is needed only
                    to escape a clipping ancestor - and this sheet has none, it sits in a plain
                    card. Keeping it in place keeps the explanation part of the row it explains,
                    in the accessibility tree and to anything reading the DOM.
                  -->
                  <sl-tooltip content=${description}>
                    <sl-icon
                      class="operation-type-info"
                      name="info-circle"
                      label="About ${label}"
                    ></sl-icon>
                  </sl-tooltip>
                `
              : nothing
          }
        </div>
      </div>
    `;
  }

  render() {
    if (this.loading && this.domains.length === 0) {
      return html`
        <div class="loading-state">
          <sl-spinner></sl-spinner>
          <span>Loading ${terms.userGroupDomain.lowerPlural}...</span>
        </div>
      `;
    }

    if (this.error) {
      return html`<sl-alert variant="danger" open>${this.error}</sl-alert>`;
    }

    const listed = this.listedDomains;
    if (listed.length === 0) {
      return html`
        <div class="empty-state">
          <span>No ${terms.userGroupDomain.lowerPlural} are available.</span>
        </div>
      `;
    }

    return html`
      ${
        this.statusMessage
          ? html`<sl-alert variant=${this.statusVariant} open closable
              >${this.statusMessage}</sl-alert
            >`
          : nothing
      }
      <div class="operation-types">
        ${listed.map((domain) => this.renderRow(domain))}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'site-operation-types-section': SiteOperationTypesSection;
  }
}
